import getTaxRateFromDefaultLockingComponent from "./getTaxRateFromDefaultLockingComponent"
import getCustomFieldsByName from "@secureo/common/utils/commerceTools/getCustomFieldsByName"
import { decodeMatrixCustomField } from "@secureo/common/utils/commerceTools/escapeMatrixCustomField"
import getLockingSystemPricePortions, {
	getLockingSystemMsrpPricePortions,
} from "./getLockingSystemPricePortions"
import { getInitialLockingSystemComponents } from "./getInitialReducerState"
import getComponentDraftsByUuidFromTextLineItems from "./getComponentDraftsByUuidFromTextLineItems"
import getComponentsByUuidFromComponentDrafts from "./getComponentsByUuidFromComponentDrafts"

import { SavedLockingSystem } from "@secureo/common/typings/LockingSystem"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import { LockingSystemCategory } from "@secureo/common/typings/Category"
import { LockingSystemReducerState } from ".."
import getMatrixType from "@secureo/common/utils/getMatrixType"

export interface ParseLockingSystemTextLineItemAttributes {
	configurationsByConfigurationGroup: string
	masterVariantSku: string
	selectedInnerLength: number
	selectedOuterLength: number
	selectedSystem: string
	sku: string
	uuid: string
	variantId: number
}

export interface ParseLockingSystemAttributes {
	keyComponentsUuids: LockingSystemReducerState["keyComponentsUuids"]
	lockingComponentsUuids: LockingSystemReducerState["keyComponentsUuids"]
	lockingSystemCategoryKey: string
	lockingSystemCategoryQuantity: number
	matrix: string[]
	type: "lockingSystem"
}

export const parseSavedLockingSystem = (savedLockingSystem: SavedLockingSystem) => {
	const { id, version, name, textLineItems = [], custom } = savedLockingSystem

	const {
		keyComponentsUuids,
		lockingComponentsUuids,
		lockingSystemCategoryQuantity: quantity,
		matrix: encodedMatrix,
		lockingSystemCategoryKey,
	} = getCustomFieldsByName(custom) as ParseLockingSystemAttributes

	const matrix = decodeMatrixCustomField(encodedMatrix)

	const componentDraftsByUuid = getComponentDraftsByUuidFromTextLineItems(textLineItems)

	const parsedSavedLockingSystem = {
		shoppingListId: id,
		shoppingListVersion: version,
		name,
		quantity,
		lockingComponentsUuids,
		keyComponentsUuids,
		matrix,
		componentDraftsByUuid,
		lockingSystemCategoryKey,
	}

	return parsedSavedLockingSystem
}

const isOrdered = false

const getInitialReducerStateFromSavedLockingSystem = async (
	accessToken: string,
	lockingSystemCategory: LockingSystemCategory,
	locale: string,
	countryCode: CountryCode,
	isDebug = false,
	savedLockingSystem: SavedLockingSystem,
	isOwner: boolean,
) => {
	if (!lockingSystemCategory) return {}

	const taxRate = getTaxRateFromDefaultLockingComponent(
		lockingSystemCategory?.defaultLockingComponent,
		countryCode,
	)

	const {
		lockingSystemType,
		defaultLockingComponentSku,
		defaultLockingComponent,
		defaultKeyComponentSku,
		defaultKeyComponent,
	} = getInitialLockingSystemComponents(lockingSystemCategory, countryCode, taxRate)

	const {
		shoppingListId,
		shoppingListVersion,
		name,
		lockingComponentsUuids,
		keyComponentsUuids,
		matrix,
		quantity,
		componentDraftsByUuid,
	} = parseSavedLockingSystem(savedLockingSystem)

	const matrixType = getMatrixType(matrix)

	const componentsByUuid = await getComponentsByUuidFromComponentDrafts(
		accessToken,
		locale,
		countryCode,
		taxRate,
		componentDraftsByUuid,
		lockingComponentsUuids,
		keyComponentsUuids,
	)

	const { singleNetPriceCents, singleGrossPriceCents, totalNetPriceCents, totalGrossPriceCents } =
		getLockingSystemPricePortions(componentsByUuid, quantity)

	const {
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
	} = getLockingSystemMsrpPricePortions(componentsByUuid, quantity)

	const isFrozen = !isOwner || isOrdered

	const initialReducerState: LockingSystemReducerState = {
		// UI state
		isLoading: false,
		isFrozen,
		isQuickStartOpen: false,

		// Locking System
		system: lockingSystemCategory.key,
		lockingSystemType,
		defaultLockingComponentSku,
		defaultLockingComponent,
		defaultKeyComponentSku,
		defaultKeyComponent,

		// Localization
		countryCode,
		locale,

		// Save / Load
		hasUnsavedChanges: false,
		shoppingListId,
		shoppingListVersion,

		// Personalization
		name,

		// Components
		componentsByUuid,
		lockingComponentsUuids,
		keyComponentsUuids,

		// Matrix
		matrix,
		matrixType,

		// Tax & Quantity
		taxRate,
		quantity,

		// Price
		singleNetPriceCents,
		singleGrossPriceCents,
		totalNetPriceCents,
		totalGrossPriceCents,

		// Msrp price
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,

		// Debugging
		isDebug,
	}

	return initialReducerState
}

export default getInitialReducerStateFromSavedLockingSystem
